import React, { useState, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  searchICD10, getCategories, searchMedications,
  addDiagnosis, updateDiagnosis, resolveDiagnosis,
  addMedication, updateMedication, discontinueMedication,
  addAllergy, removeAllergy,
  FREQUENCY_OPTIONS, ROUTE_OPTIONS, ALLERGY_SEVERITIES,
} from "../services/diagnosisService";
import {
  Search, Plus, X, AlertTriangle, Pill, Shield,
  ChevronDown, ChevronUp, Check, Clock, Edit3,
} from "lucide-react";

const S = {
  card: {
    background: "rgba(15,23,42,0.7)",
    border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(15,23,42,0.5)",
    border: "1px solid rgba(99,179,237,0.12)",
    color: "#e2e8f0",
    fontSize: 13,
    outline: "none",
  },
  btn: {
    padding: "8px 16px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    border: "none",
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 4,
    display: "block",
  },
};

// ─── Sub-sections ────────────────────────────────────────────────

function DiagnosisSection({ individual, canEdit, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDx, setSelectedDx] = useState(null);
  const [dxType, setDxType] = useState("primary");
  const [saving, setSaving] = useState(false);

  const diagnoses = individual?.diagnoses || [];
  const active = diagnoses.filter(d => d.status === "active");
  const resolved = diagnoses.filter(d => d.status === "resolved");
  const searchResults = searchICD10(searchQuery);

  const handleAdd = async () => {
    if (!selectedDx) return;
    setSaving(true);
    try {
      await addDiagnosis(individual.id, {
        ...selectedDx,
        type: dxType,
        _currentList: diagnoses,
      });
      setShowAdd(false);
      setSelectedDx(null);
      setSearchQuery("");
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error("Failed to add diagnosis:", e);
    }
    setSaving(false);
  };

  const handleResolve = async (idx) => {
    if (!window.confirm("Resolve this diagnosis?")) return;
    await resolveDiagnosis(individual.id, idx, diagnoses);
    if (onUpdate) onUpdate();
  };

  const handleSetPrimary = async (idx) => {
    // Demote current primary, promote selected
    const updated = diagnoses.map((d, i) => ({
      ...d,
      type: i === idx ? "primary" : (d.type === "primary" ? "secondary" : d.type),
    }));
    const ref = await import("firebase/firestore").then(m => m.doc(m.getFirestore(), "sharedIndividuals", individual.id));
    const { updateDoc, serverTimestamp } = await import("firebase/firestore");
    await updateDoc(ref, { diagnoses: updated, updatedAt: serverTimestamp() });
    if (onUpdate) onUpdate();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>Diagnoses</div>
        {canEdit && (
          <button onClick={() => setShowAdd(!showAdd)} style={{
            ...S.btn,
            background: showAdd ? "rgba(252,129,129,0.1)" : "rgba(99,179,237,0.1)",
            color: showAdd ? "#fc8181" : "#63b3ed",
          }}>
            {showAdd ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add Diagnosis</>}
          </button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...S.card, background: "rgba(99,179,237,0.03)", border: "1px solid rgba(99,179,237,0.15)" }}>
          <div style={S.label}>Search ICD-10 Code</div>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "#4a5568" }} />
            <input
              style={{ ...S.input, paddingLeft: 34 }}
              placeholder="Search by code or description (e.g., F32 or depression)..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSelectedDx(null); }}
              autoFocus
            />
          </div>

          {/* Search results */}
          {searchResults.length > 0 && !selectedDx && (
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, borderRadius: 10, border: "1px solid rgba(99,179,237,0.08)" }}>
              {searchResults.map((dx, i) => (
                <button key={dx.code} onClick={() => { setSelectedDx(dx); setSearchQuery(`${dx.code} — ${dx.description}`); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "10px 12px",
                    background: i % 2 === 0 ? "rgba(15,23,42,0.3)" : "rgba(15,23,42,0.5)",
                    border: "none", color: "#e2e8f0", cursor: "pointer", fontSize: 12,
                    borderBottom: "1px solid rgba(99,179,237,0.04)",
                  }}>
                  <span style={{ fontWeight: 800, color: "#63b3ed", fontFamily: "monospace" }}>{dx.code}</span>
                  <span style={{ marginLeft: 8, color: "#a0aec0" }}>{dx.description}</span>
                  <span style={{ float: "right", fontSize: 9, color: "#4a5568" }}>{dx.category}</span>
                </button>
              ))}
            </div>
          )}

          {/* Selected — choose type */}
          {selectedDx && (
            <div style={{ marginTop: 12 }}>
              <div style={{ padding: 10, borderRadius: 8, background: "rgba(104,211,145,0.06)", border: "1px solid rgba(104,211,145,0.15)", marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#68d391" }}>{selectedDx.code}</span>
                <span style={{ fontSize: 12, color: "#a0aec0", marginLeft: 8 }}>{selectedDx.description}</span>
              </div>
              <div style={S.label}>Diagnosis Type</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["primary", "secondary", "tertiary"].map(t => (
                  <button key={t} onClick={() => setDxType(t)} style={{
                    ...S.btn,
                    background: dxType === t ? "rgba(99,179,237,0.15)" : "rgba(15,23,42,0.5)",
                    color: dxType === t ? "#63b3ed" : "#718096",
                    border: `1px solid ${dxType === t ? "rgba(99,179,237,0.3)" : "rgba(99,179,237,0.06)"}`,
                    textTransform: "capitalize",
                  }}>{t}</button>
                ))}
              </div>
              <button onClick={handleAdd} disabled={saving} style={{
                ...S.btn, width: "100%", marginTop: 12, padding: 12,
                background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
                color: "white", opacity: saving ? 0.5 : 1,
              }}>
                {saving ? "Adding..." : "Add Diagnosis"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Active diagnoses */}
      {active.length === 0 && !showAdd ? (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#4a5568", fontSize: 12 }}>
          No diagnoses documented
        </div>
      ) : (
        active.map((dx, i) => {
          const origIdx = diagnoses.indexOf(dx);
          return (
            <div key={i} style={{
              ...S.card,
              borderLeft: `3px solid ${dx.type === "primary" ? "#63b3ed" : dx.type === "secondary" ? "#4fd1c5" : "#718096"}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#63b3ed", fontFamily: "monospace" }}>{dx.code}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase",
                      background: dx.type === "primary" ? "rgba(99,179,237,0.12)" : "rgba(79,209,197,0.08)",
                      color: dx.type === "primary" ? "#63b3ed" : "#4fd1c5",
                    }}>{dx.type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 4 }}>{dx.description}</div>
                  <div style={{ fontSize: 10, color: "#4a5568", marginTop: 4 }}>
                    {dx.date && `Diagnosed: ${dx.date}`}
                    {dx.diagnosedBy && ` · By: ${dx.diagnosedBy}`}
                  </div>
                </div>
                {canEdit && (
                  <div style={{ display: "flex", gap: 4 }}>
                    {dx.type !== "primary" && (
                      <button onClick={() => handleSetPrimary(origIdx)} title="Set as primary"
                        style={{ ...S.btn, padding: "4px 8px", background: "rgba(99,179,237,0.08)", color: "#63b3ed", fontSize: 10 }}>
                        Primary
                      </button>
                    )}
                    <button onClick={() => handleResolve(origIdx)} title="Resolve"
                      style={{ ...S.btn, padding: "4px 8px", background: "rgba(104,211,145,0.08)", color: "#68d391", fontSize: 10 }}>
                      Resolve
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Resolved */}
      {resolved.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Resolved ({resolved.length})
          </div>
          {resolved.map((dx, i) => (
            <div key={i} style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(15,23,42,0.3)", marginBottom: 4, opacity: 0.6 }}>
              <span style={{ fontSize: 11, fontFamily: "monospace", color: "#718096" }}>{dx.code}</span>
              <span style={{ fontSize: 11, color: "#4a5568", marginLeft: 8 }}>{dx.description}</span>
              {dx.resolvedDate && <span style={{ fontSize: 10, color: "#2d3748", marginLeft: 8 }}>Resolved: {dx.resolvedDate}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MedicationSection({ individual, canEdit, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [medName, setMedName] = useState("");
  const [dosage, setDosage] = useState("");
  const [frequency, setFrequency] = useState("");
  const [prescriber, setPrescriber] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);

  const medications = individual?.medications || [];
  const active = medications.filter(m => m.status === "active");
  const discontinued = medications.filter(m => m.status === "discontinued");
  const suggestions = searchMedications(medName);

  const handleAdd = async () => {
    if (!medName.trim()) return;
    setSaving(true);
    try {
      await addMedication(individual.id, {
        name: medName.trim(),
        dosage: dosage.trim(),
        frequency,
        prescriber: prescriber.trim(),
      }, medications);
      setShowAdd(false);
      setMedName(""); setDosage(""); setFrequency(""); setPrescriber("");
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error("Failed to add medication:", e);
    }
    setSaving(false);
  };

  const handleDiscontinue = async (idx) => {
    const reason = window.prompt("Reason for discontinuing?");
    if (reason === null) return;
    await discontinueMedication(individual.id, idx, reason, medications);
    if (onUpdate) onUpdate();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Pill size={14} style={{ color: "#f6ad55" }} />
          <span style={{ fontSize: 14, fontWeight: 800 }}>Medications</span>
          <span style={{ fontSize: 11, color: "#4a5568" }}>({active.length} active)</span>
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(!showAdd)} style={{
            ...S.btn,
            background: showAdd ? "rgba(252,129,129,0.1)" : "rgba(246,173,85,0.1)",
            color: showAdd ? "#fc8181" : "#f6ad55",
          }}>
            {showAdd ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add Med</>}
          </button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...S.card, background: "rgba(246,173,85,0.03)", border: "1px solid rgba(246,173,85,0.12)" }}>
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ position: "relative" }}>
              <div style={S.label}>Medication Name</div>
              <input style={S.input} placeholder="Start typing..." value={medName}
                onChange={e => setMedName(e.target.value)} autoFocus />
              {suggestions.length > 0 && medName.length >= 2 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "#0f1729", border: "1px solid rgba(99,179,237,0.15)", borderRadius: 8, maxHeight: 150, overflowY: "auto" }}>
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => setMedName(s)}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "none", border: "none", color: "#a0aec0", fontSize: 12, cursor: "pointer", borderBottom: "1px solid rgba(99,179,237,0.04)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={S.label}>Dosage</div>
                <input style={S.input} placeholder="e.g., 100mg" value={dosage} onChange={e => setDosage(e.target.value)} />
              </div>
              <div>
                <div style={S.label}>Frequency</div>
                <select style={{ ...S.input, appearance: "auto" }} value={frequency} onChange={e => setFrequency(e.target.value)}>
                  <option value="">Select...</option>
                  {FREQUENCY_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div style={S.label}>Prescriber</div>
              <input style={S.input} placeholder="Dr. Name" value={prescriber} onChange={e => setPrescriber(e.target.value)} />
            </div>
            <button onClick={handleAdd} disabled={saving || !medName.trim()} style={{
              ...S.btn, width: "100%", padding: 12,
              background: medName.trim() ? "linear-gradient(135deg, #dd6b20, #c05621)" : "rgba(246,173,85,0.1)",
              color: medName.trim() ? "white" : "#718096", opacity: saving ? 0.5 : 1,
            }}>
              {saving ? "Adding..." : "Add Medication"}
            </button>
          </div>
        </div>
      )}

      {/* Active meds */}
      {active.length === 0 && !showAdd ? (
        <div style={{ textAlign: "center", padding: "20px 0", color: "#4a5568", fontSize: 12 }}>
          No medications documented
        </div>
      ) : (
        active.map((med, i) => {
          const origIdx = medications.indexOf(med);
          return (
            <div key={i} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{med.name}</div>
                <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>
                  {med.dosage && `${med.dosage} · `}{med.frequency || "—"}
                </div>
                {med.prescriber && <div style={{ fontSize: 10, color: "#4a5568", marginTop: 2 }}>Rx: {med.prescriber}</div>}
                {med.startDate && <div style={{ fontSize: 10, color: "#4a5568" }}>Started: {med.startDate}</div>}
              </div>
              {canEdit && (
                <button onClick={() => handleDiscontinue(origIdx)} style={{
                  ...S.btn, padding: "6px 10px", background: "rgba(252,129,129,0.06)",
                  color: "#fc8181", fontSize: 10, border: "1px solid rgba(252,129,129,0.12)",
                }}>
                  D/C
                </button>
              )}
            </div>
          );
        })
      )}

      {/* Discontinued */}
      {discontinued.length > 0 && (
        <button onClick={() => setShowDiscontinued(!showDiscontinued)} style={{
          ...S.btn, width: "100%", marginTop: 4, background: "rgba(15,23,42,0.3)",
          color: "#4a5568", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
        }}>
          {showDiscontinued ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          Discontinued ({discontinued.length})
        </button>
      )}
      {showDiscontinued && discontinued.map((med, i) => (
        <div key={i} style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(15,23,42,0.3)", marginTop: 4, opacity: 0.6 }}>
          <span style={{ fontSize: 11, color: "#718096", textDecoration: "line-through" }}>{med.name} {med.dosage}</span>
          {med.endDate && <span style={{ fontSize: 10, color: "#4a5568", marginLeft: 8 }}>D/C: {med.endDate}</span>}
          {med.discontinueReason && <div style={{ fontSize: 10, color: "#4a5568", fontStyle: "italic" }}>Reason: {med.discontinueReason}</div>}
        </div>
      ))}
    </div>
  );
}


function AllergySection({ individual, canEdit, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [allergen, setAllergen] = useState("");
  const [reaction, setReaction] = useState("");
  const [severity, setSeverity] = useState("Moderate");
  const [saving, setSaving] = useState(false);

  const allergies = individual?.allergies || [];

  const handleAdd = async () => {
    if (!allergen.trim()) return;
    setSaving(true);
    try {
      await addAllergy(individual.id, { allergen: allergen.trim(), reaction: reaction.trim(), severity }, allergies);
      setShowAdd(false);
      setAllergen(""); setReaction(""); setSeverity("Moderate");
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error("Failed to add allergy:", e);
    }
    setSaving(false);
  };

  const handleRemove = async (idx) => {
    if (!window.confirm("Remove this allergy?")) return;
    await removeAllergy(individual.id, idx, allergies);
    if (onUpdate) onUpdate();
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={14} style={{ color: "#fc8181" }} />
          <span style={{ fontSize: 14, fontWeight: 800 }}>Allergies</span>
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(!showAdd)} style={{
            ...S.btn, background: showAdd ? "rgba(252,129,129,0.1)" : "rgba(252,129,129,0.06)",
            color: "#fc8181",
          }}>
            {showAdd ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add</>}
          </button>
        )}
      </div>

      {showAdd && (
        <div style={{ ...S.card, background: "rgba(252,129,129,0.02)", border: "1px solid rgba(252,129,129,0.1)" }}>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={S.label}>Allergen</div>
              <input style={S.input} placeholder="e.g., Penicillin" value={allergen} onChange={e => setAllergen(e.target.value)} autoFocus />
            </div>
            <div>
              <div style={S.label}>Reaction</div>
              <input style={S.input} placeholder="e.g., Rash, anaphylaxis" value={reaction} onChange={e => setReaction(e.target.value)} />
            </div>
            <div>
              <div style={S.label}>Severity</div>
              <div style={{ display: "flex", gap: 6 }}>
                {ALLERGY_SEVERITIES.map(s => (
                  <button key={s} onClick={() => setSeverity(s)} style={{
                    ...S.btn, flex: 1, fontSize: 10,
                    background: severity === s ? (s === "Life-threatening" ? "rgba(252,129,129,0.2)" : s === "Severe" ? "rgba(246,173,85,0.15)" : "rgba(99,179,237,0.1)") : "rgba(15,23,42,0.5)",
                    color: severity === s ? (s === "Life-threatening" || s === "Severe" ? "#fc8181" : "#63b3ed") : "#718096",
                    border: `1px solid ${severity === s ? "rgba(252,129,129,0.2)" : "rgba(99,179,237,0.06)"}`,
                  }}>{s}</button>
                ))}
              </div>
            </div>
            <button onClick={handleAdd} disabled={saving || !allergen.trim()} style={{
              ...S.btn, width: "100%", padding: 12,
              background: allergen.trim() ? "rgba(252,129,129,0.15)" : "rgba(252,129,129,0.05)",
              color: allergen.trim() ? "#fc8181" : "#718096", opacity: saving ? 0.5 : 1,
            }}>
              {saving ? "Adding..." : "Add Allergy"}
            </button>
          </div>
        </div>
      )}

      {allergies.length === 0 && !showAdd ? (
        <div style={{
          padding: "12px 16px", borderRadius: 10,
          background: "rgba(104,211,145,0.04)", border: "1px solid rgba(104,211,145,0.1)",
          fontSize: 12, color: "#68d391", fontWeight: 600, textAlign: "center",
        }}>
          NKDA — No Known Drug Allergies
        </div>
      ) : (
        allergies.map((a, i) => (
          <div key={i} style={{
            ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center",
            borderLeft: `3px solid ${a.severity === "Life-threatening" || a.severity === "Severe" ? "#fc8181" : a.severity === "Moderate" ? "#f6ad55" : "#ecc94b"}`,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{a.allergen}</div>
              <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>
                {a.reaction || "Reaction not specified"} ·{" "}
                <span style={{
                  fontWeight: 700,
                  color: a.severity === "Life-threatening" || a.severity === "Severe" ? "#fc8181" : "#f6ad55",
                }}>{a.severity}</span>
              </div>
            </div>
            {canEdit && (
              <button onClick={() => handleRemove(i)} style={{
                ...S.btn, padding: "4px 8px", background: "rgba(252,129,129,0.06)",
                color: "#fc8181", fontSize: 10,
              }}>
                <X size={12} />
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}


// ─── Main Tab Component ──────────────────────────────────────────

export default function DiagnosisMedTab({ individual, onUpdate }) {
  const { profile } = useAuth();
  const role = profile?.role || "agent";

  // Agents can view, clinicians/supervisors/admins/prescribers can edit
  const canEdit = ["clinician", "prescriber", "supervisor", "admin"].includes(role);

  if (!individual) return null;

  return (
    <div>
      {!canEdit && (
        <div style={{
          padding: "8px 12px", borderRadius: 10, marginBottom: 16,
          background: "rgba(236,201,75,0.04)", border: "1px solid rgba(236,201,75,0.1)",
          fontSize: 11, color: "#ecc94b",
        }}>
          Read-only view. Clinical staff can edit diagnoses and medications.
        </div>
      )}

      <DiagnosisSection individual={individual} canEdit={canEdit} onUpdate={onUpdate} />
      <MedicationSection individual={individual} canEdit={canEdit} onUpdate={onUpdate} />
      <AllergySection individual={individual} canEdit={canEdit} onUpdate={onUpdate} />
    </div>
  );
}